import { c as _c } from "react/compiler-runtime";
import type { HTMLAttributes, ReactNode } from 'react';
import { Badge as ShadcnBadge } from '@/components/ui/badge';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Public Badge wrapper — restricted variant set (default | secondary |
// destructive | outline | brand). `ghost` and `link` are excluded — zero callsite
// usage and no clear design role. The underlying shadcn primitive keeps its
// broader variant set as the cva source-of-truth.
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'brand';
export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: BadgeVariant | undefined;
  /** Layout passthrough only (positioning, alignment). Visual-token className
   *  (`bg-*`, `text-*`, `border-*`, `rounded-*`) is flagged by FQ-110 — use a
   *  variant or add `// ALLOW-RAW-BADGE-STYLE: <reason>` for genuine one-offs. */
  className?: string | undefined;
  children?: ReactNode | undefined;
}

// Morfi semantic tokens (replace stock's text-xs + rounded-full).
// `rounded-pill` couples Badge to the same capsule token as sort/filter pills
// so a future shape change is a single token edit.
const BADGE_MORFI_TOKENS = 'rounded-pill text-caption' as const;
export function Badge(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "brand" ? "secondary" : variant;
  const t3 = variant === "brand" && "border-border-brand bg-background-brand text-foreground-brand";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(BADGE_MORFI_TOKENS, t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== props || $[10] !== t2 || $[11] !== t4 || $[12] !== variant) {
    t5 = <ShadcnBadge variant={t2} data-variant={variant} className={t4} {...props}>{children}</ShadcnBadge>;
    $[8] = children;
    $[9] = props;
    $[10] = t2;
    $[11] = t4;
    $[12] = variant;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}